/* eslint-disable react-hooks/exhaustive-deps */
import { CircularProgress } from '@mui/material';
import { useCallback, useContext, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { StatusCode } from '../../api/enumerations';
import {
  createAccountResetPassword,
  resetPassword,
} from '../../api/resetPassword';
import { IconVisibility, PasswordIcon } from '../../components/InputIcon';
import Snackbar from '../../components/Snackbar';
import { WhiteCircularProgress } from '../../components/UI/Typography';
import { GlobalContext } from '../../context/global';
import { validatePassword } from '../../helpers';
import { removeLocalStorageToken } from '../../helpers/localStorage';
import useErrorMessage from '../../hooks/useErrorMessage';
import useSnackbar from '../../hooks/useSnackbar';
import { Constants } from './constants';
import {
  ContainerGrid,
  ExternalInputBox,
  FilledButton,
  InputBox,
  LoginGrid,
  LogoGrid,
  LogoImg,
  StyledTextField,
  Title,
} from './styles';

export default function ResetPassword({ logo }: { logo: string }): JSX.Element {
  const [initialLoading, setInitialLoading] = useState(true);
  const [token, setToken] = useState('');
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState(false);
  const [confirmation, setConfirmation] = useState('');
  const [confirmationError, setConfirmationError] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [loading, setLoading] = useState(false);

  const { getErrorMessage } = useErrorMessage();
  const { handleSnackbar } = useSnackbar();
  const { openSnackbar } = useContext(GlobalContext);

  const navigate = useNavigate();
  const location = useLocation();

  const isNewPassword = location.pathname.includes('/new-password');
  const search = new URLSearchParams(location.search);
  const accessToken = search.get('token');

  const navigateLogin = (): void => navigate('/login');

  useEffect(() => {
    removeLocalStorageToken();
  }, []);

  const handleNewToken = useCallback(async () => {
    if (!accessToken) {
      handleSnackbar('Algo deu errado, tente novamente.', true);
      navigateLogin();
      return;
    }

    try {
      const response = await createAccountResetPassword(accessToken);

      if (response.detail.description)
        throw new Error(response.detail.description);

      if (!response.data) throw new Error('Algo deu errado, tente novamente.');

      setToken(response.data.token);
    } catch (error) {
      handleSnackbar(getErrorMessage(error), true);
      navigateLogin();
    } finally {
      setInitialLoading(false);
    }
  }, [accessToken]);

  useEffect(() => {
    handleNewToken();
  }, [handleNewToken]);

  const handleSubmit = useCallback(
    async (e: React.FormEvent): Promise<void> => {
      e.preventDefault();
      setLoading(true);

      if (!token) {
        handleSnackbar('Algo deu errado, tente novamente.', true);
        navigateLogin();
        return;
      }

      const passwordData = new FormData();
      passwordData.append('password', password);

      try {
        const response = await resetPassword(token, passwordData);

        if (response.detail.description)
          throw new Error(response.detail.description);

        if (response.detail.status_code !== StatusCode.OK)
          throw new Error('Algo deu errado, tente novamente.');

        handleSnackbar('Senha alterada com sucesso!', false);
      } catch (error) {
        handleSnackbar(getErrorMessage(error), true);
      } finally {
        setLoading(false);
      }
    },
    [token, password, confirmation]
  );

  return (
    <ContainerGrid container>
      <LogoGrid item xs={6.5} xl={7.5}>
        <LogoImg>
          <img src={logo} alt="Logo" />
        </LogoImg>
      </LogoGrid>
      <LoginGrid item xs={5.5} xl={4.5}>
        {initialLoading ? (
          <CircularProgress />
        ) : (
          <ExternalInputBox>
            <Title>
              {isNewPassword ? Constants.newPassword : Constants.resetPassword}
            </Title>
            <InputBox
              component="form"
              id="set-password"
              onSubmit={handleSubmit}
            >
              <StyledTextField
                required
                type={showPassword ? 'text' : 'password'}
                variant="standard"
                helperText={password && Constants.passwordHelp}
                InputProps={{
                  startAdornment: <PasswordIcon color="#8B8C8E" />,
                  endAdornment: (
                    <IconVisibility
                      showPassword={showPassword}
                      setShowPassword={setShowPassword}
                      color="#8B8C8E"
                    />
                  ),
                }}
                id="password"
                placeholder={Constants.password}
                error={passwordError}
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  !validatePassword(e.target.value)
                    ? setPasswordError(true)
                    : setPasswordError(false);
                }}
              />
              <StyledTextField
                required
                type={showConfirmation ? 'text' : 'password'}
                variant="standard"
                helperText={confirmation && Constants.confirmHelp}
                InputProps={{
                  startAdornment: <PasswordIcon color="#8B8C8E" />,
                  endAdornment: (
                    <IconVisibility
                      showPassword={showConfirmation}
                      setShowPassword={setShowConfirmation}
                      color="#8B8C8E"
                    />
                  ),
                }}
                id="confirmation"
                placeholder={Constants.confirmPassword}
                error={confirmationError}
                value={confirmation}
                onChange={(e) => {
                  setConfirmation(e.target.value);
                  e.target.value !== password
                    ? setConfirmationError(true)
                    : setConfirmationError(false);
                }}
              />
              <FilledButton
                form="set-password"
                type="submit"
                disabled={passwordError || confirmationError || loading}
              >
                {loading ? (
                  <WhiteCircularProgress size={22} />
                ) : (
                  Constants.confirm
                )}
              </FilledButton>
            </InputBox>
          </ExternalInputBox>
        )}
      </LoginGrid>
      {openSnackbar && <Snackbar />}
    </ContainerGrid>
  );
}
